import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ApiSuccessEnvelope,
  OperatorAffiliateDetail,
  PatchAffiliateRequest,
} from "../../types/affiliates";
import { getPrivateInstance } from "../_privateInstance";

export type PatchAffiliateInput = {
  affiliateId: string;
  payload: PatchAffiliateRequest;
};

// PATCH wraps the detail one level deeper than GET.
type PatchAffiliateResponseData = {
  affiliate: OperatorAffiliateDetail;
  new_plan_version_id: string | null;
};

const patchAffiliate = async ({
  affiliateId,
  payload,
}: PatchAffiliateInput): Promise<OperatorAffiliateDetail> => {
  const path = API_ROUTES.PATCH_AFFILIATE.replace(
    "{affiliate_id}",
    encodeURIComponent(affiliateId),
  );
  const response = await getPrivateInstance().patch<
    ApiSuccessEnvelope<PatchAffiliateResponseData>
  >(path, payload);
  return response.data.data.affiliate;
};

export const usePatchAffiliate = (): UseMutationResult<
  OperatorAffiliateDetail,
  Error,
  PatchAffiliateInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["affiliates", "patch"],
    mutationFn: patchAffiliate,
    onSuccess: (data, variables) => {
      // Don't invalidate the 3-seg key — DDB read-after-write can return
      // stale data and clobber what we just wrote.
      queryClient.setQueryData(
        ["affiliates", "detail", variables.affiliateId],
        data,
      );
      // Date-scoped variants need a fresh GET for their kpis block.
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === "affiliates" &&
            key[1] === "detail" &&
            key[2] === variables.affiliateId &&
            key.length > 3
          );
        },
      });
      queryClient.invalidateQueries({ queryKey: ["affiliates", "list"] });
    },
  });
};

export { patchAffiliate };
